import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import config from '@/config/user.env';
import { getDictList } from '@/api/userApi';
import bigLogData from '@/utils/bigLogData';
import { queryOverduePopup, type OverduePopupResponse } from '@/api/overdueReasonsForceControlReminder';
import {
  OVERDUE_REASONS_FORCE_CONTROL_REMINDER,
  OVERDUE_REASONS_FORCE_CONTROL_REMINDER_DEFAULT_INTERVAL_MINUTES,
  OVERDUE_REASONS_FORCE_CONTROL_REMINDER_ENABLE,
  OVERDUE_REASONS_FORCE_CONTROL_REMINDER_EXPORT_CODE,
  OVERDUE_REASONS_FORCE_CONTROL_REMINDER_INTERVAL,
  OVERDUE_REASONS_FORCE_CONTROL_REMINDER_LAST_SHOW_STORAGE_KEY,
  OVERDUE_REASONS_FORCE_CONTROL_REMINDER_TASK_CENTER_PATH,
  OVERDUE_REASONS_FORCE_CONTROL_REMINDER_BATCH_QUERY_MAX,
} from '@/config/overdueReasonsForceControlReminder';
import { submitReportExport } from '@/utils/reportExport';
import { getLocal, setLocal } from '@/utils/storage';
import { EL_ROLE, isMatchedRoleKinds } from '@/utils/roleUtils';

export interface OverdueReasonsForceControlReminderPollConfig {
  enabled: boolean;
  intervalMinutes: number;
}

const parseIntervalMinutes = (value: unknown): number => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return OVERDUE_REASONS_FORCE_CONTROL_REMINDER_DEFAULT_INTERVAL_MINUTES;
  }
  return Math.floor(minutes);
};

export const fetchOverdueReasonsForceControlReminderPollConfig =
  async (): Promise<OverdueReasonsForceControlReminderPollConfig> => {
    try {
      const dicRes = await getDictList({ dictCodes: OVERDUE_REASONS_FORCE_CONTROL_REMINDER });
      if (+dicRes.code === 0 && dicRes.data) {
        const dict = dicRes.data[OVERDUE_REASONS_FORCE_CONTROL_REMINDER] || {};
        return {
          enabled: dict[OVERDUE_REASONS_FORCE_CONTROL_REMINDER_ENABLE] === '1',
          intervalMinutes: parseIntervalMinutes(dict[OVERDUE_REASONS_FORCE_CONTROL_REMINDER_INTERVAL]),
        };
      }
    } catch {
      // ignore
    }
    return { enabled: false, intervalMinutes: OVERDUE_REASONS_FORCE_CONTROL_REMINDER_DEFAULT_INTERVAL_MINUTES };
  };

const parseOverduePopup = (res: OverduePopupResponse | null | undefined) => {
  if (!res || +res.code !== 0 || !res.data) {
    return { count: 0, taskNoList: [] as string[] };
  }
  const { overdueTaskCount, taskNoList } = res.data;
  const taskNos = Array.isArray(taskNoList) ? taskNoList : [];
  const count = taskNos.length > 0 ? taskNos.length : Number(overdueTaskCount);
  return {
    count: Number.isFinite(count) && count > 0 ? count : 0,
    taskNoList: taskNos,
  };
};

/** 逾期强控弹窗埋点 model_name3 */
const OVERDUE_REMINDER_TRACK = {
  DIALOG_SHOW: '弹窗',
  DIALOG_CLOSE: '关闭弹窗',
  GO_REMARK: '去备注',
  DOWNLOAD: '下载',
} as const;

type LastShowStorage = Record<string, number>;

const getCurrentUserCode = (store: ReturnType<typeof useStore>): string => {
  const userCode = store.getters.user?.userCode;
  return typeof userCode === 'string' ? userCode.trim() : '';
};

const readLastShowStorage = (): LastShowStorage => {
  const cache = getLocal(OVERDUE_REASONS_FORCE_CONTROL_REMINDER_LAST_SHOW_STORAGE_KEY);
  if (!cache || typeof cache !== 'object') {
    return {};
  }
  return cache as LastShowStorage;
};

const getLastShowTime = (userCode: string): number | null => {
  const lastShowAt = readLastShowStorage()[userCode];
  return Number.isFinite(lastShowAt) && lastShowAt > 0 ? lastShowAt : null;
};

const setLastShowTime = (userCode: string, timestamp: number) => {
  const cache = readLastShowStorage();
  cache[userCode] = timestamp;
  setLocal(OVERDUE_REASONS_FORCE_CONTROL_REMINDER_LAST_SHOW_STORAGE_KEY, cache);
};

const isPollIntervalElapsed = (lastShowAt: number | null, intervalMinutes: number): boolean => {
  if (lastShowAt == null) {
    return true;
  }
  return Date.now() - lastShowAt >= intervalMinutes * 60 * 1000;
};

export function useOverdueReasonsForceControlReminderPoll() {
  const router = useRouter();
  const store = useStore();
  const dialogVisible = ref(false);
  const closeTipVisible = ref(false);
  const taskCount = ref(0);
  const taskNoList = ref<string[]>([]);
  const pollMinutes = ref(OVERDUE_REASONS_FORCE_CONTROL_REMINDER_DEFAULT_INTERVAL_MINUTES);
  const downloading = ref(false);

  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const sendOverdueReminderTrack = (modelName3: string) => {
    const { curRole, roleList } = store.state;
    const roleItem = roleList.find((item: { roleCode: string }) => item.roleCode === curRole);
    const roleName = roleItem?.roleName ?? '';
    let childModuleName = config.otpChildModuleName ?? '';
    if (roleName && childModuleName) {
      childModuleName = `${childModuleName}-${roleName}`;
    }
    bigLogData.sendDetailMessage({
      modelInfo: {
        model_name1: '订单工作台-城配',
        model_name2: '逾期强控',
        model_name3: modelName3,
      },
      childModuleName,
    });
  };

  const clearPollTimer = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const closeDialog = (trackClose = true) => {
    if (trackClose && dialogVisible.value) {
      sendOverdueReminderTrack(OVERDUE_REMINDER_TRACK.DIALOG_CLOSE);
    }
    dialogVisible.value = false;
  };

  const handleDialogClose = () => {
    closeDialog(true);
    closeTipVisible.value = true;
  };

  const closeTipDialog = () => {
    closeTipVisible.value = false;
  };

  const showDialogIfNeeded = (count: number, taskNos: string[], intervalMinutes: number) => {
    if (count <= 0) {
      return;
    }
    taskCount.value = count;
    taskNoList.value = taskNos;
    if (dialogVisible.value || closeTipVisible.value) {
      return;
    }

    const userCode = getCurrentUserCode(store);
    if (!userCode) {
      return;
    }

    const lastShowAt = getLastShowTime(userCode);
    if (!isPollIntervalElapsed(lastShowAt, intervalMinutes)) {
      return;
    }

    dialogVisible.value = true;
    setLastShowTime(userCode, Date.now());
    sendOverdueReminderTrack(OVERDUE_REMINDER_TRACK.DIALOG_SHOW);
  };

  const pollOnce = async (intervalMinutes: number) => {
    try {
      const res = await queryOverduePopup();
      const { count, taskNoList: taskNos } = parseOverduePopup(res);
      showDialogIfNeeded(count, taskNos, intervalMinutes);
    } catch {
      // 轮询失败不打断后续定时任务
    }
  };

  const startPolling = async () => {
    // 判断当前角色编码是否匹配定义的角色编码数组
    if (!isMatchedRoleKinds([EL_ROLE.ORDER_CLERK, EL_ROLE.CITY_ORDER_CLERK])) { // 不匹配则不进行轮询
      return;
    }
    clearPollTimer();
    const pollConfig = await fetchOverdueReasonsForceControlReminderPollConfig();
    pollMinutes.value = pollConfig.intervalMinutes;
    if (!pollConfig.enabled) {
      return;
    }
    await pollOnce(pollConfig.intervalMinutes);
    pollTimer = setInterval(() => {
      pollOnce(pollMinutes.value);
    }, pollConfig.intervalMinutes * 60 * 1000);
  };

  const handleGoRemark = () => {
    sendOverdueReminderTrack(OVERDUE_REMINDER_TRACK.GO_REMARK);
    const taskNos = taskNoList.value
    .map((no) => String(no).trim())
    .filter(Boolean)
    .slice(0, OVERDUE_REASONS_FORCE_CONTROL_REMINDER_BATCH_QUERY_MAX);

    if (taskNos.length > 0) {
      router.push({
        path: OVERDUE_REASONS_FORCE_CONTROL_REMINDER_TASK_CENTER_PATH,
        query: {
          isRefresh: 'true',
          batchQueryKey: 'taskNo',
          batchQueryValue: taskNos.join(','),
        },
      });
    }
    closeDialog(false);
  };

  const handleDownload = async () => {
    if (downloading.value) {
      return;
    }
    const taskNos = taskNoList.value;
    if (taskNos.length === 0) {
      closeDialog();
      return;
    }

    sendOverdueReminderTrack(OVERDUE_REMINDER_TRACK.DOWNLOAD);
    downloading.value = true;
    try {
      const currentTenant = store.state.currentTenant || store.state.user?.currentTenant;
      await submitReportExport({
        reportCode: OVERDUE_REASONS_FORCE_CONTROL_REMINDER_EXPORT_CODE,
        searchData: {
          taskNoList: taskNos,
          tenantCode: currentTenant?.tenantCode,
        },
      });
    } catch {
      // submitReportExport 内部已提示
    } finally {
      downloading.value = false;
      closeDialog(false);
    }
  };

  onMounted(() => {
    startPolling();
  });

  onUnmounted(() => {
    clearPollTimer();
  });

  return {
    dialogVisible,
    closeTipVisible,
    taskCount,
    taskNoList,
    pollMinutes,
    downloading,
    closeDialog,
    handleDialogClose,
    closeTipDialog,
    handleGoRemark,
    handleDownload,
  };
}
